import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ChevronRight,
  FileText,
  Hourglass,
  Landmark,
  Percent,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import {
  CollectionsChart,
  type CollectionMonth,
} from '@/components/finance-overview/CollectionsChart'
import { getJSON } from '@/lib/api'
import { ViewOnlyBadge } from '@/components/templates/app-shell'
import { displayMonthDay, displayMonthYear } from '@/lib/dates'

interface PeriodDelta {
  text: string
  direction: 'up' | 'down' | 'flat'
  comparison: string
}

interface FinanceOverview {
  starts_on: string
  ends_on: string
  collected: string
  collected_cents: number
  collected_delta: PeriodDelta | null
  invoiced: string
  invoiced_cents: number
  invoiced_delta: PeriodDelta | null
  open_receivables: string
  open_receivables_cents: number
  overdue: string
  overdue_cents: number
  collection_months: CollectionMonth[]
  last_payout: {
    amount: string
    amount_cents: number
    arrival_date: string
    status: string
  } | null
  fees: {
    processing: string
    processing_cents: number
    platform: string
    platform_cents: number
    total: string
    total_cents: number
  }
  needs_attention: {
    total: number
    drift: number
    unmapped: number
    failed_autopay: number
    payouts: number
  }
  recent_activity: RecentActivityItem[]
}

interface RecentActivityItem {
  event: string
  detail: string
  amount: string
  amount_cents: number
  occurred_on: string
  href: string
  money_out: boolean
}

interface FinanceOverviewResponse {
  finance_overview: FinanceOverview
}

interface MoneyCardProps {
  icon: LucideIcon
  label: string
  value: string
  detail: ReactNode
  links: Array<{ href: string; label: string }>
  successIcon?: boolean
}

export function FinanceOverviewPage() {
  const overviewQuery = useQuery({
    queryKey: ['finance-overview-money-figures'],
    queryFn: () => getJSON<FinanceOverviewResponse>('/api/v1/finance_overview_money_figures'),
    retry: false,
  })

  if (overviewQuery.isLoading) {
    return <EmptyState title="Loading finance overview" className="min-h-52" />
  }
  if (overviewQuery.isError || !overviewQuery.data) {
    return <EmptyState title="Finance overview is unavailable" className="min-h-52" />
  }

  const overview = overviewQuery.data.finance_overview

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header>
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold text-foreground">Finance</h1>
          <ViewOnlyBadge />
        </div>
        <p className="text-caption text-muted-foreground">
          {monthScope(overview.starts_on)} · Association clock
        </p>
      </header>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <MoneyCard
          icon={Wallet}
          label="Collected this month"
          value={overview.collected}
          detail={<DeltaDetail delta={overview.collected_delta} />}
          links={[{ href: '/finance/collections-report', label: 'Collections report' }]}
        />
        <MoneyCard
          icon={FileText}
          label="Invoiced this month"
          value={overview.invoiced}
          detail={<DeltaDetail delta={overview.invoiced_delta} />}
          links={[{ href: '/finance/income-report', label: 'Income report' }]}
        />
        <MoneyCard
          icon={Hourglass}
          label="Open receivables"
          value={overview.open_receivables}
          detail={
            <span
              className={overview.overdue_cents > 0 ? 'text-destructive' : 'text-muted-foreground'}
            >
              {overview.overdue} overdue
            </span>
          }
          links={[{ href: '/finance/aging-report', label: 'Aging report' }]}
        />
        <MoneyCard
          icon={Landmark}
          label="Last payout"
          value={overview.last_payout?.amount ?? '—'}
          detail={
            overview.last_payout ? (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                Arrived {displayMonthDay(overview.last_payout.arrival_date)}
                <span aria-hidden="true">·</span>
                <Badge variant={overview.last_payout.status === 'Recorded' ? 'neutral' : 'warning'}>
                  {overview.last_payout.status}
                </Badge>
              </span>
            ) : (
              <span className="text-muted-foreground">No payouts yet</span>
            )
          }
          links={[{ href: '/finance/payouts', label: 'Payouts' }]}
        />
        <MoneyCard
          icon={Percent}
          label="Fees this month"
          value={overview.fees.total}
          detail={
            <span className="text-muted-foreground">
              Processing {overview.fees.processing} · Platform {overview.fees.platform}
            </span>
          }
          links={[{ href: '/finance/payouts', label: 'Payouts' }]}
        />
        <MoneyCard
          icon={AlertTriangle}
          label="Needs attention"
          value={String(overview.needs_attention.total)}
          detail={
            <span className="text-muted-foreground">
              {attentionBreakdown(overview.needs_attention)}
            </span>
          }
          links={[
            { href: '/settings/accounting', label: 'Accounting' },
            { href: '/finance/autopay-recovery', label: 'Failed Autopay' },
            { href: '/finance/payouts', label: 'Payout review' },
          ]}
          successIcon={overview.needs_attention.total === 0}
        />
      </div>
      <div className="mt-4 space-y-4">
        <CollectionsChart months={overview.collection_months} />
        <RecentActivity items={overview.recent_activity} />
      </div>
    </div>
  )
}

function RecentActivity({ items }: { items: RecentActivityItem[] }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-body font-medium text-foreground">Recent activity</h2>
        {items.length > 0 && (
          <a
            className="text-caption text-primary hover:underline"
            href="/finance/financial-history"
          >
            View all
          </a>
        )}
      </div>
      {items.length === 0 ? (
        <EmptyState variant="quiet" title="No activity yet." />
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {items.map((item) => (
            <li key={`${item.event}-${item.occurred_on}-${item.href}`}>
              <a
                href={item.href}
                className="flex items-center justify-between gap-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="min-w-0">
                  <span className="block text-body-sm text-foreground">{item.event}</span>
                  <span className="block text-caption text-muted-foreground">
                    {item.detail} · {displayMonthDay(item.occurred_on)}
                  </span>
                </span>
                <span
                  className={`shrink-0 text-right text-body-sm tabular-nums ${
                    item.money_out ? 'text-destructive' : 'text-foreground'
                  }`}
                >
                  {item.amount}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

const DELTA_COLOR = {
  up: 'text-success',
  down: 'text-destructive',
  flat: 'text-muted-foreground',
} as const

function DeltaDetail({ delta }: { delta: PeriodDelta | null }) {
  if (!delta) return <span className="text-muted-foreground">—</span>

  return (
    <>
      <span className={DELTA_COLOR[delta.direction]}>{delta.text}</span>
      <span className="text-muted-foreground"> {delta.comparison}</span>
    </>
  )
}

function MoneyCard({
  icon: Icon,
  label,
  value,
  detail,
  links,
  successIcon = false,
}: MoneyCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-md ${
              successIcon ? 'bg-success/10 text-success' : 'bg-primary/10 text-primary'
            }`}
          >
            <Icon aria-hidden="true" className="h-4 w-4" />
          </span>
          <span className="text-caption text-muted-foreground">{label}</span>
        </div>
        <div className="mt-3 text-heading-sm font-semibold tabular-nums text-foreground">
          {value}
        </div>
        <div className="mt-1 min-h-5 text-caption">{detail}</div>
        <div className="mt-3 border-t border-border pt-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {links.map(({ href, label }) => (
              <a
                key={`${href}-${label}`}
                href={href}
                className="inline-flex items-center gap-1 text-caption text-muted-foreground hover:text-foreground"
              >
                {label}
                <ChevronRight aria-hidden="true" className="h-3 w-3" />
              </a>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function attentionBreakdown(attention: FinanceOverview['needs_attention']) {
  if (attention.total === 0) return 'All clear'

  return [
    `${attention.drift} drift`,
    `${attention.unmapped} unmapped`,
    `${attention.failed_autopay} failed Autopay`,
    `${attention.payouts} ${attention.payouts === 1 ? 'payout' : 'payouts'}`,
  ].join(' · ')
}

function monthScope(startsOn: string) {
  return displayMonthYear(startsOn)
}
